'use client';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import useScrollReveal from '@/hooks/useScrollReveal';

interface Service {
  title: string;
  icon: string;
  problemItSolves: string;
  whatChanges: string;
  accentColor: string;
  emoji: string;
  image: string;
  imageAlt: string;
}

export default function ServicesGrid() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal<HTMLElement>();

  const services = [
  {
    title: 'Brand Positioning',
    icon: 'SparklesIcon',
    emoji: '🎯',
    problemItSolves: 'Your market doesn\'t understand what makes you different. You\'re competing on price because your positioning isn\'t clear. Potential clients can\'t articulate why they should choose you over competitors.',
    whatChanges: 'Texas Tree Transformations went from "tree service" to positioned subcontractor partner with clear emergency response differentiation. Entre Capital clarified loan product positioning that converted borrowers faster. Maple & Bluebonnet established brand identity that turned pop-up customers into loyal followers.',
    accentColor: 'brand-teal',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1127f8c21-1767604853201.png",
    imageAlt: 'Strategic brand positioning workshop with team collaboration'
  },
  {
    title: 'Web Development',
    icon: 'CodeBracketIcon',
    emoji: '💻',
    problemItSolves: 'Your website is costing you credibility and conversions. It\'s slow, looks outdated, or doesn\'t work on mobile. Every visitor who bounces is lost revenue because your digital storefront doesn\'t match the quality of your work.',
    whatChanges: 'PEP got a complete website rebuild that handles volunteer onboarding and donation infrastructure. Vitality Tree Care launched with SEO-optimized architecture that generates leads. RPM Diesel Services received a custom Progressive Web App that streamlined service intake and QuickBooks integration. Texas Tree Scout App enables GPS-based field logging with structured reporting.',
    accentColor: 'brand-green',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_180e59a00-1772190970020.png",
    imageAlt: 'Web development workspace with code and design mockups'
  },
  {
    title: 'Mobile App Development',
    icon: 'DevicePhoneMobileIcon',
    emoji: '📱',
    problemItSolves: 'Your team works in the field but your tools don\'t. Spreadsheets break down on job sites. Your customers expect a mobile experience and you\'re sending them to a desktop website that barely loads on a phone. You\'re losing efficiency and credibility every day without a proper mobile solution.',
    whatChanges: 'RPM Diesel Services got a Progressive Web App (PWA) that works offline, streamlined service intake from the shop floor, and integrated directly with QuickBooks—eliminating paper-based workflows. Texas Tree Scout App was built with Flutter to enable GPS-based field logging, photo capture, and structured reporting for arborists in the field. Both apps reduced manual data entry and gave teams tools that actually work where the work happens.',
    accentColor: 'brand-plum',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e5977c72-1764746786012.png",
    imageAlt: 'Mobile app development showing Flutter and React Native apps on smartphones'
  },
  {
    title: 'SEO & Search Growth',
    icon: 'MagnifyingGlassIcon',
    emoji: '🔍',
    problemItSolves: 'Nobody can find you when they search for what you do. You\'re invisible in your market. Competitors with worse services rank higher because they invested in SEO years ago and you\'re starting from zero.',
    whatChanges: 'Texas Tree Transformations built sustainable organic visibility through SEO growth strategy. PEP\'s ongoing SEO management drives volunteer and donor discovery. Good Riddance Cleaning ranks for NFPA Code 96 certified kitchen exhaust cleaning. Meraki Builder\'s blog architecture generates consistent search traffic. Cream Coffee Shop\'s content strategy increased local search presence.',
    accentColor: 'brand-orange',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1895ffe79-1772596273334.png",
    imageAlt: 'SEO analytics dashboard showing organic growth metrics'
  },
  {
    title: 'Automation & Workflow Systems',
    icon: 'BoltIcon',
    emoji: '⚡',
    problemItSolves: 'You\'re spending 15-20 hours per week on repetitive tasks that should be automated. Follow-ups get missed. Data entry errors cost you money. Your team is drowning in administrative work instead of revenue-generating activities.',
    whatChanges: 'Texas Tree Transformations automated scheduling, follow-ups, and reporting workflows. RPM Diesel Services built notification automation via email and SMS that eliminated manual coordination. Texas Tree Scout App structured field reporting workflows that saved hours per week. Operational chaos became predictable systems.',
    accentColor: 'brand-plum',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cb73e084-1773207964259.png",
    imageAlt: 'Automated workflow system dashboard with integrations'
  },
  {
    title: 'CRM Integration & Data Systems',
    icon: 'CircleStackIcon',
    emoji: '🔗',
    problemItSolves: 'Customer data is scattered across spreadsheets, email, and three different tools. Your team can\'t see the full customer picture. Opportunities fall through cracks because there\'s no centralized system tracking relationships.',
    whatChanges: 'Texas Tree Transformations integrated CRM that centralized customer data and automated follow-through. PEP\'s Salesforce integration strategy connected volunteer, donor, and program data. RPM Diesel Services built QuickBooks estimate creation integration that eliminated double data entry and reduced errors.',
    accentColor: 'brand-teal',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12b9908b5-1766986667777.png",
    imageAlt: 'CRM integration dashboard showing connected systems'
  },
  {
    title: 'Content Strategy & Production',
    icon: 'DocumentTextIcon',
    emoji: '📝',
    problemItSolves: 'You post inconsistently because you don\'t have a content system. Every piece of content is created from scratch. You have no strategic narrative connecting your marketing efforts, so nothing compounds over time.',
    whatChanges: 'Texas Tree Transformations produces weekly marketing emails and drone-based content that showcases expertise. Entre Capital\'s HTML email builds and borrower-facing content drive conversions. Maple & Bluebonnet\'s social content direction and seasonal storytelling built community. Consistent content became a growth engine, not a monthly scramble.',
    accentColor: 'brand-orange',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13e23bdfd-1764656055734.png",
    imageAlt: 'Content strategy planning with editorial calendar'
  }];


  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-sm sm:text-base font-semibold tracking-wider text-brand-plum">Our Services</h2>
          <p className="mt-2 text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-brand-offwhite">
            What We Actually Do
          </p>
          <p className="mt-4 sm:mt-6 text-base sm:text-lg font-normal text-text-secondary px-4">
            Seven core services. Real outcomes from actual client work. No generic promises.
          </p>
        </div>
        <div className="space-y-10 sm:space-y-12 md:space-y-16">
          {services.map((service, index) => {
            const { ref: serviceRef, isVisible: serviceVisible } = useScrollReveal<HTMLDivElement>();
            return (
              <div
                key={index}
                ref={serviceRef}
                className={`group grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden shadow-xl transition-all duration-1000 hover:shadow-2xl ${
                index % 2 === 0 ? '' : 'lg:grid-flow-dense'} ${serviceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`
                }
                style={{ transitionDelay: `${index * 100}ms` }}>
                
                  {/* Image Column */}
                  <div className={`relative h-[300px] sm:h-[350px] md:h-[400px] lg:h-[500px] overflow-hidden ${
                index % 2 === 0 ? '' : 'lg:col-start-2'}`
                }>
                      <AppImage
                    src={service.image}
                    alt={service.imageAlt}
                    width={800}
                    height={500}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />
                  
                      {/* Color accent bar */}
                      <div className={`absolute top-0 left-0 w-1 h-full bg-${service.accentColor} group-hover:w-2 transition-all duration-300`}></div>
                    </div>
                    
                    {/* Content Column */}
                    <div className={`bg-brand-offwhite p-6 sm:p-8 md:p-10 flex flex-col justify-center ${
                index % 2 === 0 ? '' : 'lg:col-start-1 lg:row-start-1'}`
                }>
                        <div className="space-y-3 sm:space-y-4">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-${service.accentColor} flex items-center justify-center transition-all duration-300 group-hover:scale-110`}>
                              <Icon name={service.icon} size={20} className="text-white sm:w-6 sm:h-6" />
                            </div>
                            <span className="text-2xl sm:text-3xl">{service.emoji}</span>
                          </div>
                          
                          <h3 className={`font-headline text-xl sm:text-2xl md:text-3xl text-brand-plum font-bold border-l-4 border-${service.accentColor} pl-3 sm:pl-4`}>
                            {service.title}
                          </h3>

                          <div className="space-y-3 sm:space-y-4 pl-3 sm:pl-4">
                            <div>
                              <h4 className={`font-body font-bold text-[10px] sm:text-xs text-${service.accentColor} mb-1.5 sm:mb-2 uppercase tracking-wider`}>
                                The Problem It Solves
                              </h4>
                              <p className="font-body text-xs sm:text-sm text-text-secondary leading-relaxed">
                                {service.problemItSolves}
                              </p>
                            </div>

                            <div className="pt-2 sm:pt-3 border-t-2 border-brand-plum">
                              <h4 className="font-body font-bold text-[10px] sm:text-xs text-brand-plum mb-1.5 sm:mb-2 uppercase tracking-wider">
                                Real Client Outcomes
                              </h4>
                              <p className="font-body text-xs sm:text-sm text-brand-plum font-semibold leading-relaxed">
                                {service.whatChanges}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>);

          })}
        </div>
      </div>
    </section>);

}